import {
  useCustomMutation,
  useInvalidate,
  useNotification,
  useTranslate,
} from '@refinedev/core';
import { Button, Popconfirm, Tooltip } from 'antd';
import { RedoOutlined } from '@ant-design/icons';
import type {
  ContentProviderImportOut,
  ContentProviderImportReimportOut,
} from '@/api/generated/schemas';
import { extractBackendMessage } from '@/utils/apiError';

const RESOURCE = 'content_catalog.provider_imports';
const T = 'content_catalog.provider_imports.reimport';

interface ReimportButtonProps {
  providerImport: ContentProviderImportOut;
}

export const ReimportButton = ({ providerImport }: ReimportButtonProps) => {
  const t = useTranslate();
  const { open: notify } = useNotification();
  const invalidate = useInvalidate();
  const { mutate, mutation } =
    useCustomMutation<ContentProviderImportReimportOut>();

  // Reimport replays the stored file; nothing to replay without one.
  const hasFile = providerImport.s3_object_reference_id != null;

  const handleConfirm = () =>
    mutate(
      {
        url: `content_catalog/provider_imports/${providerImport.id}/reimport`,
        method: 'post',
        values: {},
        successNotification: false,
        errorNotification: false,
      },
      {
        onSuccess: ({ data }) => {
          notify?.({
            type: 'success',
            message: t('notifications.success'),
            description: t(`${T}.success`, { id: data.provider_import_id }),
          });
          invalidate({ resource: RESOURCE, invalidates: ['list'] });
        },
        onError: (error) => {
          notify?.({
            type: 'error',
            message: t('notifications.error'),
            description:
              extractBackendMessage(error) ?? t(`${T}.error_fallback`),
          });
        },
      }
    );

  return (
    <Tooltip title={hasFile ? undefined : t(`${T}.no_file`)}>
      <Popconfirm
        title={t(`${T}.confirm_title`)}
        description={t(`${T}.confirm_description`)}
        okText={t(`${T}.button`)}
        cancelText={t('buttons.cancel')}
        onConfirm={handleConfirm}
        disabled={!hasFile}
      >
        <Button
          size="small"
          icon={<RedoOutlined />}
          loading={mutation.isPending}
          disabled={!hasFile}
        >
          {t(`${T}.button`)}
        </Button>
      </Popconfirm>
    </Tooltip>
  );
};
